import {
  Check,
  Home,
  Circle,
  RotateCcw,
  Route,
  ShieldCheck,
} from "lucide-react";
import { getModule } from "../content/course";

type CourseLayoutProps = {
  requiredModuleIds: string[];
  completedModuleIds: string[];
  currentModuleId?: string;
  assessmentActive: boolean;
  storageAvailable: boolean;
  onNavigate: (moduleId: string) => void;
  onHome: () => void;
  onReset: () => void;
  children: React.ReactNode;
};

export function CourseLayout({
  requiredModuleIds,
  completedModuleIds,
  currentModuleId,
  assessmentActive,
  storageAvailable,
  onNavigate,
  onHome,
  onReset,
  children,
}: CourseLayoutProps) {
  const completeCount = requiredModuleIds.filter((moduleId) =>
    completedModuleIds.includes(moduleId),
  ).length;
  const overallSteps = requiredModuleIds.length + 1;
  const progressPercent = Math.round(
    ((completeCount + (assessmentActive ? 0.5 : 0)) / overallSteps) * 100,
  );
  const firstIncompleteIndex = requiredModuleIds.findIndex(
    (moduleId) => !completedModuleIds.includes(moduleId),
  );
  const unlockedThrough =
    firstIncompleteIndex === -1 ? requiredModuleIds.length - 1 : firstIncompleteIndex;

  return (
    <div className="course-shell">
      <aside className="course-sidebar">
        <div className="sidebar-brand">
          <span className="brand-symbol">
            <Route size={21} />
          </span>
          <div>
            <strong>AI Day</strong>
            <span>Crash course</span>
          </div>
        </div>

        <div className="sidebar-progress">
          <div>
            <span>Course progress</span>
            <strong>{progressPercent}%</strong>
          </div>
          <div
            className="progress-track"
            role="progressbar"
            aria-label="Course progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progressPercent}
          >
            <span style={{ width: `${progressPercent}%` }} />
          </div>
        </div>

        <nav aria-label="Course modules">
          <ol className="module-nav">
            {requiredModuleIds.map((moduleId, index) => {
              const module = getModule(moduleId);
              const completed = completedModuleIds.includes(moduleId);
              const current = currentModuleId === moduleId;
              const locked = index > unlockedThrough;
              return (
                <li key={moduleId}>
                  <button
                    type="button"
                    className={current ? "current" : ""}
                    disabled={locked}
                    aria-current={current ? "step" : undefined}
                    onClick={() => onNavigate(moduleId)}
                  >
                    <span className="module-status">
                      {completed ? (
                        <Check size={15} />
                      ) : (
                        <Circle size={13} fill={current ? "currentColor" : "none"} />
                      )}
                    </span>
                    <span>
                      <strong>{module.shortTitle}</strong>
                      <small>{module.durationMinutes} min</small>
                    </span>
                  </button>
                </li>
              );
            })}
            <li>
              <button
                type="button"
                className={assessmentActive ? "current" : ""}
                disabled={completeCount !== requiredModuleIds.length}
              >
                <span className="module-status">
                  <ShieldCheck size={15} />
                </span>
                <span>
                  <strong>Knowledge check</strong>
                  <small>10 min</small>
                </span>
              </button>
            </li>
          </ol>
        </nav>

        <div className="sidebar-route">
          <span>Current section</span>
          <strong>{currentModuleId ? getModule(currentModuleId).shortTitle : "Knowledge check"}</strong>
          <button type="button" onClick={onHome}>
            <Home size={15} /> Course overview
          </button>
        </div>

        <button className="reset-button" type="button" onClick={onReset}>
          <RotateCcw size={15} /> Reset course
        </button>
      </aside>

      <div className="course-main">
        {!storageAvailable ? (
          <div className="storage-warning" role="status">
            Browser storage is unavailable. You can continue, but progress will
            be lost when this tab closes.
          </div>
        ) : null}
        <main id="main-content">{children}</main>
      </div>
    </div>
  );
}
